import { useCurrentOrgUser, useOrg, useOrgController } from "../../auth/org-provider";
import { useLinkOauthAccountMutation } from "../mutations/link-oauth-account";
import { OAuthService } from "tekkr-common/dist/oauth";

export function useConnectOAuthService(force: boolean = false): {
    isPending: boolean;
    connect: (service: OAuthService) => Promise<void>;
} {
    const orgController = useOrgController();
    const mutation = useLinkOauthAccountMutation();
    const currentUser = useCurrentOrgUser();

    return {
        isPending: mutation.isPending,
        connect: (service) => {
            if (currentUser.linkedAccounts.includes(service) && !force) {
                // already connected
                return Promise.resolve();
            }
            return new Promise((resolve) => {
                mutation.mutateAsync({ service }).then((url) => {
                    const w = window.open(url);
                    const i = setInterval(async () => {
                        if (w?.closed) {
                            void orgController.refetch();
                            // todo re-check here if we're actually connected
                            clearInterval(i);
                            resolve();
                        }
                    }, 500);
                });
            });
        },
    };
}

export function useConnectOfficeSuiteProvider(force?: boolean): {
    isPending: boolean;
    connect: () => Promise<void>;
} {
    const org = useOrg();
    const base = useConnectOAuthService(force);
    return {
        isPending: base.isPending,
        connect: () => {
            let service: OAuthService;
            if (org.officeSuiteProvider === "google") {
                service = OAuthService.Google;
            } else if (org.officeSuiteProvider === "microsoft") {
                service = OAuthService.Microsoft;
            } else {
                throw new Error(`unknown office suite provider ${org.officeSuiteProvider}`);
            }
            return base.connect(service);
        },
    };
}

// wrap some sort of action (a function) in some logic that connects the given OAuth service before executing the action
export function useOAuthServiceAction(
    service: OAuthService,
    action: () => Promise<void> | void
): {
    action: () => Promise<void>;
    serviceConnected: boolean;
    isPending: boolean;
} {
    const currentUser = useCurrentOrgUser();
    const serviceConnected = currentUser.linkedAccounts.includes(service);

    const { isPending, connect } = useConnectOAuthService();

    return {
        serviceConnected,
        isPending: isPending,
        action: async () => {
            await connect(service);
            await action();
        },
    };
}
